import { useState } from "react";
import { format, parseISO, subYears } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { todayIST } from "@/lib/api";

const DROPDOWN_CLASSES = {
  caption_label: "text-sm font-medium flex items-center gap-1",
  caption_dropdowns: "flex gap-3",
  dropdown_month: "relative inline-flex items-center rounded-md border px-2 py-1",
  dropdown_year: "relative inline-flex items-center rounded-md border px-2 py-1",
  dropdown: "absolute inset-0 w-full opacity-0 cursor-pointer",
  vhidden: "sr-only",
};

export const toDDMMYYYY = (ymd) => (ymd ? format(parseISO(ymd), "dd/MM/yyyy") : "");

export const DatePicker = ({ value, onChange, min, max, dob, clearable, testId, placeholder = "dd/mm/yyyy", className = "" }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const today = parseISO(todayIST());
  const maxD = dob ? subYears(today, 18) : max ? parseISO(max) : undefined;
  const minD = dob ? subYears(today, 100) : min ? parseISO(min) : undefined;
  const disabled = [minD && { before: minD }, maxD && { after: maxD }].filter(Boolean);
  const pick = (d) => {
    onChange(d ? format(d, "yyyy-MM-dd") : "");
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId}
            className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-white px-3 text-sm text-left hover:border-[#0D5C46] transition-colors">
            <CalendarDays size={16} className="text-[#0D5C46] shrink-0" />
            <span className={`font-mono ${value ? "text-slate-900" : "text-slate-400"}`}>{value ? toDDMMYYYY(value) : placeholder}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" collisionPadding={12} data-testid={testId ? `${testId}-calendar` : undefined}>
          <Calendar mode="single" selected={selected} onSelect={pick} disabled={disabled}
            defaultMonth={selected || (dob ? subYears(today, 25) : minD)}
            {...(dob ? { captionLayout: "dropdown-buttons", fromDate: minD, toDate: maxD, classNames: DROPDOWN_CLASSES } : {})} />
        </PopoverContent>
      </Popover>
      {clearable && value && (
        <button type="button" aria-label="Clear date" data-testid={testId ? `${testId}-clear` : undefined} onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-rose-600"><X size={14} /></button>
      )}
    </div>
  );
};
